import { formatPrice, type AuctionCurrency } from "./currency.js";
import type { Lot } from "./types.js";

/**
 * What an organiser should look at in a pool's lots. Every one of these is advice: nothing is
 * refused, a lot with warnings saves like any other, and whether any should stop the auction
 * from starting is the auction's call.
 */
export interface LotWarning {
  kind: "uneven" | "unassigned" | "unpriced" | "purse";
  message: string;
  lotId?: string;
  squadId?: string;
}

export interface SquadPurse {
  squadId: string;
  /** The team's name, for the message. */
  name: string;
  purse: number;
}

export interface LotWarningInput {
  lots: readonly Lot[];
  /** Bidding players in no lot. */
  unassigned: number;
  /** How many teams share the pool: its squads. */
  teams: number;
  purses: readonly SquadPurse[];
  /** The tournament's currency, so a purse is read in the unit the room bids in. */
  currency?: AuctionCurrency;
}

/**
 * What to call a lot: its name, or "Lot n" for its place in the order when it has none. The
 * number is a place and not an identity, so an unnamed lot is renumbered when lots are reordered.
 * One function, so the warnings and both screens call a lot the same thing.
 */
export function lotLabel(lot: Pick<Lot, "name">, index: number): string {
  return lot.name ?? `Lot ${index + 1}`;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const possessive = (name: string) => (name.endsWith("s") ? `${name}'` : `${name}'s`);

/**
 * The least a team must spend: for every lot, the whole number of its players each team is
 * owed times the lot's base price, with a lot that has no price counting as zero. Exact when the
 * lots divide evenly; for an uneven lot it leaves out whichever team gets the extra player,
 * which is a bidding rule and not decided here. A captain's own cost is not counted.
 */
export function minimumSpend(lots: readonly Lot[], teams: number): number {
  if (teams <= 0) return 0;
  return lots.reduce(
    (total, lot) => total + Math.floor(lot.players.length / teams) * (lot.basePrice ?? 0),
    0,
  );
}

/** The warnings for a pool, in the order the lots run, then the unassigned, then the purses. */
export function lotWarnings({
  lots,
  unassigned,
  teams,
  purses,
  currency,
}: LotWarningInput): LotWarning[] {
  const warnings: LotWarning[] = [];
  const ordered = [...lots].sort((a, b) => a.position - b.position);

  ordered.forEach((lot, index) => {
    const label = lotLabel(lot, index);

    // With no teams there is nothing to divide by, and the warning would say nothing useful.
    if (teams > 0 && lot.players.length % teams !== 0) {
      warnings.push({
        kind: "uneven",
        lotId: lot.id,
        message: `${label} has ${plural(lot.players.length, "player", "players")} for ${plural(teams, "team", "teams")}, which does not divide evenly.`,
      });
    }

    if (lot.basePrice === null) {
      warnings.push({ kind: "unpriced", lotId: lot.id, message: `${label} has no base price.` });
    }
  });

  if (unassigned > 0) {
    warnings.push({
      kind: "unassigned",
      message: `${plural(unassigned, "player is", "players are")} in no lot.`,
    });
  }

  const price = (n: number) => (currency ? formatPrice(n, currency) : String(n));
  const minimum = minimumSpend(ordered, teams);
  if (minimum > 0) {
    for (const squad of purses) {
      if (squad.purse < minimum) {
        warnings.push({
          kind: "purse",
          squadId: squad.squadId,
          message: `${possessive(squad.name)} purse of ${price(squad.purse)} is below the ${price(minimum)} it must spend.`,
        });
      }
    }
  }

  return warnings;
}
